import globalAxios from './axiosInstance';

const API_GATEWAY_BASE_URL = process.env.REACT_APP_BLINKPAY_API_GATEWAY_URL;

const camelToSnake = (str: string): string => {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

const snakeToCamel = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const convertKeysToSnakeCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Date) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => convertKeysToSnakeCase(item));
    }

    if (typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const snakeKey = camelToSnake(key);
            acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
            return acc;
        }, {} as any);
    }

    return obj;
};

const convertKeysToCamelCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Date) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => convertKeysToCamelCase(item));
    }

    if (typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const camelKey = snakeToCamel(key);
            acc[camelKey] = convertKeysToCamelCase(obj[key]);
            return acc;
        }, {} as any);
    }

    return obj;
};

export const createConsent = async (data: any) => {
    try {
        const snakeCaseData = convertKeysToSnakeCase(data);
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/v1/consents`, snakeCaseData);
        console.log('Consent triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering consent:', error);
        throw error;
    }
};

export const getAccounts = async (consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts?consent_id=${consentId}`);
        console.log('Accounts triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering accounts:', error);
        throw error;
    }
};

export const getAccount = async (accountId: any, consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}?consent_id=${consentId}`);
        console.log('Account triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering account:', error);
        throw error;
    }
};

export const getBalances = async (accountId: any, consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/balances?consent_id=${consentId}`);
        console.log('Account balances retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving account balances:', error);
        throw error;
    }
};

export const getParty = async (accountId: any, consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/party?consent_id=${consentId}`);
        console.log('Account party retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving account party:', error);
        throw error;
    }
};

export const getTransactions = async (accountId: any, consentId: any, fromBookingDateTime: any, toBookingDateTime: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/transactions?consent_id=${consentId}&from_booking_date_time=${fromBookingDateTime}&to_booking_date_time=${toBookingDateTime}`);
        console.log('Account transactions retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving account transactions:', error);
        throw error;
    }
};

export const getStatements = async (accountId: any, consentId: any, fromStatementDateTime: any, toStatementDateTime: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/statements?consent_id=${consentId}&from_statement_date_time=${fromStatementDateTime}&to_statement_date_time=${toStatementDateTime}`);
        console.log('Account statements retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving account statements:', error);
        throw error;
    }
};

export const getStatement = async (accountId: any, statementId: any, consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/statements/${statementId}?consent_id=${consentId}`);
        console.log('Account statement retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving account statement:', error);
        throw error;
    }
};

export const downloadStatement = async (accountId: any, statementId: any, consentId: any) => {
    try {
        const response = await globalAxios.get(`${API_GATEWAY_BASE_URL}/v1/accounts/${accountId}/statements/${statementId}/file?consent_id=${consentId}`, {
            responseType: 'blob' // Handle binary PDF data and text
        });
        console.log('Account statement file retrieved successfully');

        // Check content type to determine if it's PDF or plain text
        const contentType = response.headers['content-type'];

        if (contentType && contentType.includes('text/plain')) {
            // Convert blob to text for plain text responses
            const text = await response.data.text();
            return { data: text, type: 'text' };
        } else {
            // Return blob for PDF or other binary files
            return { data: response.data, type: 'pdf' };
        }
    } catch (error) {
        console.error('Error retrieving account statement file:', error);
        throw error;
    }
};